import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import AppBreadcrumbs from "@/components/AppBreadcrumbs";
import {
  ArrowRight,
  Buildings,
  ClockCounterClockwise,
  MagnifyingGlass,
  Phone,
  Plus,
  Users,
} from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function relationshipScoreTone(value) {
  const score = Math.max(0, Math.min(100, Number(value) || 0));
  if (score >= 85) return "excellent";
  if (score >= 70) return "good";
  if (score >= 55) return "caution";
  if (score >= 40) return "warning";
  return "danger";
}

function RelationshipScoreBadge({ value }) {
  const numericValue = Number(value);
  const hasScore = Number.isFinite(numericValue);
  const score = hasScore ? Math.max(0, Math.min(100, Math.round(numericValue))) : null;
  const tone = hasScore ? relationshipScoreTone(score) : "empty";
  const label = hasScore ? `Relationship score ${score}` : "Relationship score not available";

  return (
    <span
      className={`customer-card__score customer-card__score--${tone}`}
      aria-label={label}
      title={label}
    >
      {hasScore ? score : "—"}
    </span>
  );
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState(() => sessionStorage.getItem("callsect:customers:query") || "");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/customers");
      setCustomers(data);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    sessionStorage.setItem("callsect:customers:query", query);
  }, [query]);

  useEffect(() => {
    const saveScroll = () => {
      sessionStorage.setItem("callsect:customers:scroll", String(window.scrollY));
    };
    window.addEventListener("scroll", saveScroll, { passive: true });
    return () => {
      saveScroll();
      window.removeEventListener("scroll", saveScroll);
    };
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    const savedScroll = Number(sessionStorage.getItem("callsect:customers:scroll") || 0);
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: savedScroll, left: 0 }));
    return () => window.cancelAnimationFrame(frame);
  }, [loading]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredCustomers = customers.filter((customer) => (
    !normalizedQuery
    || (customer.name || "").toLowerCase().includes(normalizedQuery)
    || (customer.company || "").toLowerCase().includes(normalizedQuery)
  ));
  const totalCalls = customers.reduce((sum, customer) => sum + (customer.call_count || 0), 0);

  return (
    <div className="customers-page">
      <Nav />

      <main className="customers-shell">
        <AppBreadcrumbs items={[{ label: "Customers" }]} />

        <header className="customers-hero">
          <div className="customers-hero__copy">
            <div className="brand-eyebrow">Customers</div>
            <h1>Relationship timelines</h1>
            <p>
              Every customer&apos;s arc, at a glance. Six months from now,
              one click is all you need.
            </p>

            <div className="customers-hero__summary" aria-label="Customer timeline summary">
              <span>
                <Users size={15} weight="duotone" />
                {customers.length} relationship{customers.length === 1 ? "" : "s"}
              </span>
              <span>
                <Phone size={15} weight="duotone" />
                {totalCalls} call{totalCalls === 1 ? "" : "s"} mapped
              </span>
            </div>
          </div>

          <Link to="/upload" className="customers-primary-button">
            <Plus size={15} weight="bold" />
            Upload a call
          </Link>
        </header>

        <section className="customers-directory">
          <div className="customers-directory__toolbar">
            <div>
              <div className="customers-section-label">Customer memory</div>
              <h2>Relationship library</h2>
            </div>

            {!loading && customers.length > 0 && (
              <label className="customers-search">
                <MagnifyingGlass size={16} aria-hidden="true" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search customers or companies..."
                  aria-label="Search customers or companies"
                  data-testid="customer-search-input"
                />
                <span className="customers-search__count">
                  {filteredCustomers.length}/{customers.length}
                </span>
              </label>
            )}
          </div>

          <div className="segmented-divider" />

          {loading ? (
            <div className="customers-loading" aria-label="Loading customers">
              {[0, 1, 2, 3].map((item) => (
                <div className="customers-skeleton" key={item}>
                  <div className="customers-skeleton__mark" />
                  <div className="customers-skeleton__copy">
                    <i />
                    <i />
                  </div>
                </div>
              ))}
            </div>
          ) : customers.length === 0 ? (
            <div className="customers-empty">
              <div className="customers-empty__icon">
                <Users size={26} weight="duotone" />
              </div>
              <strong>No customer timelines yet</strong>
              <p>
                Upload a call with a customer name to start building a
                longitudinal relationship history.
              </p>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="customers-empty customers-empty--compact">
              <div className="customers-empty__icon">
                <MagnifyingGlass size={24} />
              </div>
              <strong>No matching relationships</strong>
              <p>Try a different customer or company name.</p>
            </div>
          ) : (
            <div className="customers-list">
              {filteredCustomers.map((customer, index) => (
                <Link
                  key={customer.key}
                  to={`/customers/${encodeURIComponent(customer.key)}`}
                  state={{ from: "customers" }}
                  data-testid={`customer-card-${customer.key}`}
                  className="customer-card"
                  style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
                  aria-label={`Open timeline for ${customer.name}`}
                >
                  <RelationshipScoreBadge value={customer.relationship_score} />

                  <span className="customer-card__content">
                    <span className="customer-card__name">{customer.name}</span>
                    <span className="customer-card__meta">
                      {customer.company && (
                        <span>
                          <Buildings size={13} />
                          {customer.company}
                        </span>
                      )}
                      <span>
                        <Phone size={13} />
                        {customer.call_count} call{customer.call_count === 1 ? "" : "s"}
                      </span>
                    </span>
                  </span>

                  <span className="customer-card__dates">
                    <span>
                      <small>First call</small>
                      {formatDate(customer.first_call_at)}
                    </span>
                    <span>
                      <small>Latest call</small>
                      {formatDate(customer.last_call_at)}
                    </span>
                  </span>

                  <span className="customer-card__open">
                    Timeline
                    <ArrowRight size={14} weight="bold" />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>

        {!loading && customers.length > 0 && (
          <div className="customers-footnote">
            <ClockCounterClockwise size={14} weight="duotone" />
            Timelines update automatically as new calls are analyzed.
          </div>
        )}
      </main>
    </div>
  );
}
